import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { API, BACKEND_HOST } from "../../../api";
import { Button, Input, Label, Modal } from "@/Components/ui";
import { GripVertical, Image as ImageIcon, Save, Trash2, Upload } from "lucide-react";

import DeleteCarouselModal from "./DeleteCarouselModal";

interface CarouselApiImage {
    key: number;
    src: string;
    link_url?: string | null;
    title?: string | null;
    description?: string | null;
}

interface DraftCarouselImage {
    tempId: string;
    id?: number;
    src: string;
    uploadSource?: string;
    link_url: string;
    title: string;
    description: string;
}

interface CarouselModalProps {
    modalNumber: number;
    carouselId: number;
    setVisible: (visible: boolean) => void;
    visible: boolean;
    site: number;
    carouselData?: any[];
    onSave?: () => void;
    carouselType?: "main" | "gallery";
    titleLabel?: string;
    description?: string;
}

const createTempId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const readFileAsDataURL = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const toDraftImage = (image: CarouselApiImage): DraftCarouselImage => ({
    tempId: `existing-${image.key}`,
    id: image.key,
    src: image.src,
    link_url: image.link_url || "",
    title: image.title || "",
    description: image.description || "",
});

const CarouselModal: React.FC<CarouselModalProps> = ({
    modalNumber,
    carouselId,
    setVisible,
    visible,
    onSave,
    carouselType = "main",
    titleLabel = "히어로 캐러셀",
    description,
}) => {
    const [images, setImages] = useState<DraftCarouselImage[]>([]);
    const [draggedImageId, setDraggedImageId] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);

    const isCreateMode = modalNumber < 0;
    const helperDescription = description || (
        carouselType === "main"
            ? "히어로 캐러셀 그룹은 1개만 유지되며, 그룹 안에서는 이미지를 여러 장 추가, 삭제, 정렬할 수 있습니다."
            : "갤러리 그룹은 여러 개 생성할 수 있으며, 각 그룹 안에서 이미지를 여러 장 추가, 삭제, 정렬할 수 있습니다."
    );

    useEffect(() => {
        if (!visible) {
            return;
        }

        if (isCreateMode) {
            setIsLoading(false);
            setImages([]);
            return;
        }

        if (!carouselId || carouselId === -1) {
            return;
        }

        setIsLoading(true);
        API.get(`${BACKEND_HOST}/sites/carousel/${carouselId}/`)
            .then((res) => {
                setImages((res.data.images || []).map(toDraftImage));
            })
            .catch((error) => {
                console.error(error);
                toast.error(`${titleLabel} 이미지를 불러오지 못했습니다.`);
            })
            .finally(() => setIsLoading(false));
    }, [carouselId, isCreateMode, titleLabel, visible]);

    const closeModal = (force = false) => {
        if (!force && isProcessing) {
            return;
        }
        setImages([]);
        setDraggedImageId(null);
        setDeleteModal(false);
        setVisible(false);
    };

    const handleSelectFiles = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        if (files.length === 0) {
            return;
        }

        try {
            const previews = await Promise.all(
                files.map(async (file) => {
                    const src = await readFileAsDataURL(file);
                    return {
                        tempId: createTempId(),
                        src,
                        uploadSource: src,
                        link_url: "",
                        title: "",
                        description: "",
                    };
                })
            );
            setImages((prev) => [...prev, ...previews]);
        }
        catch (error) {
            console.error(error);
            toast.error("이미지를 읽는 중 오류가 발생했습니다.");
        }
        finally {
            if (event.target) {
                event.target.value = "";
            }
        }
    };

    const handleDragStart = (tempId: string) => {
        setDraggedImageId(tempId);
    };

    const handleDragEnd = () => {
        setDraggedImageId(null);
    };

    const handleDrop = (targetTempId: string) => {
        if (!draggedImageId || draggedImageId === targetTempId) {
            return;
        }

        setImages((prev) => {
            const draggedIndex = prev.findIndex(
                (image) => image.tempId === draggedImageId
            );
            const targetIndex = prev.findIndex(
                (image) => image.tempId === targetTempId
            );

            if (draggedIndex === -1 || targetIndex === -1) {
                return prev;
            }

            const next = [...prev];
            const [draggedItem] = next.splice(draggedIndex, 1);
            next.splice(targetIndex, 0, draggedItem);
            return next;
        });
        setDraggedImageId(null);
    };

    const handleFieldChange = (
        tempId: string,
        field: keyof Pick<DraftCarouselImage, "title" | "link_url" | "description">,
        value: string
    ) => {
        setImages((prev) =>
            prev.map((image) =>
                image.tempId === tempId ? { ...image, [field]: value } : image
            )
        );
    };

    const handleRemoveImage = (tempId: string) => {
        setImages((prev) => prev.filter((image) => image.tempId !== tempId));
    };

    const createCarousel = async () => {
        const payload = images
            .map((image) => image.uploadSource)
            .filter((image): image is string => Boolean(image));

        if (payload.length === 0) {
            toast.error("등록할 이미지를 먼저 추가해주세요.");
            return;
        }

        setIsProcessing(true);
        toast.promise(
            API.post(`${BACKEND_HOST}/sites/carousels/`, {
                type: carouselType,
                images: payload,
            }),
            {
                loading: `${titleLabel} 그룹을 생성하는 중...`,
                success: () => {
                    if (onSave) {
                        onSave();
                    }
                    closeModal(true);
                    return `${titleLabel} 그룹이 생성되었습니다.`;
                },
                error: (error) => {
                    console.error(error);
                    return `${titleLabel} 그룹 생성에 실패했습니다.`;
                },
            }
        ).finally(() => setIsProcessing(false));
    };

    const syncCarousel = async () => {
        setIsProcessing(true);
        toast.promise(
            API.put(`${BACKEND_HOST}/sites/carousels/${carouselId}/`, {
                images: images.map((image) => ({
                    id: image.id,
                    src: image.uploadSource || image.src,
                    url: image.link_url || null,
                    title: image.title || null,
                    description: image.description || null,
                })),
            }),
            {
                loading: `${titleLabel} 이미지를 저장하는 중...`,
                success: () => {
                    if (onSave) {
                        onSave();
                    }
                    closeModal(true);
                    return `${titleLabel} 이미지가 저장되었습니다.`;
                },
                error: (error) => {
                    console.error(error);
                    return `${titleLabel} 이미지 저장에 실패했습니다.`;
                },
            }
        ).finally(() => setIsProcessing(false));
    };

    return (
        <React.Fragment>
            <Modal isOpen={visible} onClose={closeModal} centered size="lg">
                <div className="py-4">
                    <div className="mb-4">
                        <h5 className="mb-1">
                            {isCreateMode
                                ? `${titleLabel} 그룹 생성`
                                : `${titleLabel} 그룹 이미지 관리`}
                        </h5>
                        <p className="text-muted small mb-0">
                            {helperDescription}
                        </p>
                        <p className="text-muted small mb-0">
                            저장 시 현재 화면에 보이는 이미지 배열 기준으로 전체 동기화됩니다.
                        </p>
                    </div>

                    <div className="mb-4">
                        <Label
                            htmlFor={`carouselUpload-${carouselType}`}
                            className="d-flex align-items-center justify-content-center gap-2 p-3 border border-dashed rounded cursor-pointer text-primary"
                            style={{ cursor: "pointer", borderStyle: "dashed" }}
                        >
                            <Upload size={20} />
                            <span>이미지 업로드 (여러 장 선택 가능)</span>
                        </Label>
                        <Input
                            id={`carouselUpload-${carouselType}`}
                            className="form-control"
                            type="file"
                            accept="image/*"
                            onChange={handleSelectFiles}
                            multiple
                            disabled={isProcessing}
                        />
                    </div>

                    <div className="border rounded">
                        {isLoading ? (
                            <div className="text-center text-muted py-5">
                                이미지를 불러오는 중입니다...
                            </div>
                        ) : images.length > 0 ? (
                            images.map((image, index) => (
                                <div
                                    key={image.tempId}
                                    draggable={!isProcessing}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDragStart={() => handleDragStart(image.tempId)}
                                    onDragEnd={handleDragEnd}
                                    onDrop={() => handleDrop(image.tempId)}
                                    className="d-flex align-items-center gap-3 p-3 border-bottom bg-white"
                                    style={{
                                        cursor: isProcessing ? "not-allowed" : "grab",
                                        opacity: draggedImageId === image.tempId ? 0.65 : 1,
                                    }}
                                >
                                    <div
                                        className="text-muted"
                                        style={{
                                            cursor: isProcessing ? "not-allowed" : "grab",
                                        }}
                                    >
                                        <GripVertical size={20} />
                                    </div>

                                    <img
                                        src={image.src}
                                        alt={`${titleLabel} 이미지 ${index + 1}`}
                                        className="rounded border"
                                        style={{
                                            width: "72px",
                                            height: "72px",
                                            objectFit: "cover",
                                        }}
                                    />

                                    <div className="flex-grow-1">
                                        <div className="mb-2 text-muted small">
                                            {index + 1}번째 이미지
                                        </div>
                                        <div className="d-flex gap-2 mb-2">
                                            <Input
                                                type="text"
                                                placeholder="제목 (선택)"
                                                value={image.title}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        image.tempId,
                                                        "title",
                                                        event.target.value
                                                    )
                                                }
                                                className="form-control-sm"
                                                disabled={isProcessing}
                                            />
                                        </div>
                                        <div className="d-flex gap-2 mb-2">
                                            <Input
                                                type="text"
                                                placeholder="클릭 시 이동할 URL (선택)"
                                                value={image.link_url}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        image.tempId,
                                                        "link_url",
                                                        event.target.value
                                                    )
                                                }
                                                className="form-control-sm"
                                                disabled={isProcessing}
                                            />
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Input
                                                type="text"
                                                placeholder="설명 (선택)"
                                                value={image.description}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        image.tempId,
                                                        "description",
                                                        event.target.value
                                                    )
                                                }
                                                className="form-control-sm"
                                                disabled={isProcessing}
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        color="soft-danger"
                                        size="sm"
                                        onClick={() => handleRemoveImage(image.tempId)}
                                        disabled={isProcessing}
                                    >
                                        <Trash2 size={14} className="mr-1" />
                                        삭제
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-muted py-5">
                                <ImageIcon size={48} className="mb-2" />
                                <p className="mb-0">등록된 이미지가 없습니다</p>
                                <small>위 버튼을 클릭하여 이미지를 추가하세요</small>
                            </div>
                        )}
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                        {!isCreateMode ? (
                            <Button
                                color="soft-danger"
                                onClick={() => setDeleteModal(true)}
                                disabled={isProcessing}
                            >
                                그룹 전체 삭제
                            </Button>
                        ) : (
                            <div />
                        )}
                        <div className="d-flex gap-2">
                            <Button
                                color="primary"
                                onClick={() => {
                                    if (isCreateMode) {
                                        createCarousel();
                                        return;
                                    }
                                    syncCarousel();
                                }}
                                disabled={isProcessing || (isCreateMode && images.length === 0)}
                            >
                                <Save size={14} className="mr-1" />
                                저장
                            </Button>
                            <Button color="light" onClick={closeModal} disabled={isProcessing}>
                                닫기
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {!isCreateMode ? (
                <DeleteCarouselModal
                    type={0}
                    carouselId={carouselId}
                    comment={`${titleLabel} 그룹을`}
                    deleteModal={deleteModal}
                    setDeleteModal={setDeleteModal}
                    setCarouselModal={setVisible}
                    onSave={onSave}
                />
            ) : null}
        </React.Fragment>
    );
};

export default CarouselModal;
